// bookie-blush-preview — Blush + rose paper invitation card.
//
// Mood: soft late-spring, magnolia, dawn light. The outer frame is a
// warm blush gradient; the inner card sits on the template's pale
// blush paper with rose ink and a magnolia-bloom ornament centred
// above the names. Eyebrow + caption use the template's rose accent.
import { type LayoutCtx } from "./_shared";

export function BookieBlushPreviewLayout({
  names,
  date,
  venue,
  accent,
  ink,
  bg,
  font,
}: LayoutCtx) {
  // Blush foil — warm rose / champagne alternation. Lighter than the
  // rose-gold reference but in the same family.
  const FOIL =
    "linear-gradient(135deg, #E8B6B6 0%, #F2D2C6 26%, #F7E0D1 50%, #F2D2C6 75%, #E8B6B6 100%)";

  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        background: FOIL,
        padding: "5%",
        display: "flex",
      }}
    >
      <div
        style={{
          flex: 1,
          background: bg,
          color: ink,
          padding: "8%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
          boxShadow: `inset 0 0 0 1px ${accent}55`,
          position: "relative",
        }}
      >
        {/* Eyebrow */}
        <div
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: 7,
            fontWeight: 700,
            letterSpacing: "0.4em",
            color: accent,
            marginBottom: "5%",
          }}
        >
          TAKLIFNOMA
        </div>

        {/* Magnolia bloom medallion */}
        <div
          style={{
            width: "60%",
            aspectRatio: "1 / 1",
            position: "relative",
            marginBottom: "5%",
          }}
        >
          <svg
            viewBox="0 0 120 120"
            style={{ width: "100%", height: "100%", display: "block" }}
            aria-hidden
          >
            <defs>
              <radialGradient id="bkbl-petal" cx="0.5" cy="0.35" r="0.7">
                <stop offset="0%" stopColor={accent} stopOpacity="0.55" />
                <stop offset="100%" stopColor={accent} stopOpacity="0.18" />
              </radialGradient>
            </defs>
            {/* Hairline ring */}
            <circle cx="60" cy="60" r="56" fill="none" stroke={accent} strokeOpacity="0.45" strokeWidth="0.8" />
            <circle cx="60" cy="60" r="52" fill="none" stroke={accent} strokeOpacity="0.22" strokeWidth="0.4" />

            {/* Six magnolia petals around the centre */}
            {Array.from({ length: 6 }, (_, i) => {
              const angle = i * 60;
              return (
                <g key={i} transform={`translate(60 60) rotate(${angle})`}>
                  <path
                    d="M0 0 C -8 -14 -6 -28 0 -38 C 6 -28 8 -14 0 0 Z"
                    fill="url(#bkbl-petal)"
                    stroke={accent}
                    strokeOpacity="0.55"
                    strokeWidth="0.5"
                  />
                </g>
              );
            })}

            {/* Inner petal halo — slightly smaller, offset rotation */}
            {Array.from({ length: 6 }, (_, i) => {
              const angle = i * 60 + 30;
              return (
                <g key={`b-${i}`} transform={`translate(60 60) rotate(${angle})`}>
                  <path
                    d="M0 0 C -5 -8 -4 -18 0 -24 C 4 -18 5 -8 0 0 Z"
                    fill={accent}
                    fillOpacity="0.45"
                  />
                </g>
              );
            })}

            {/* Centre stamen */}
            <circle cx="60" cy="60" r="4" fill={accent} fillOpacity="0.9" />
            <circle cx="60" cy="60" r="1.8" fill={bg} />
          </svg>
        </div>

        {/* Names */}
        <div
          style={{
            fontFamily: font,
            fontSize: 30,
            lineHeight: 1.05,
            fontWeight: 400,
            color: ink,
          }}
        >
          {names}
        </div>

        <div style={{ flex: 1 }} />

        {/* Bottom caption */}
        <div
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: 6.5,
            fontWeight: 700,
            letterSpacing: "0.32em",
            color: accent,
            marginTop: "4%",
          }}
        >
          TAKLIFNOMA · {date}
        </div>
        <div
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: 6,
            fontWeight: 600,
            letterSpacing: "0.28em",
            color: `${ink}99`,
            marginTop: 6,
          }}
        >
          {venue.toUpperCase()}
        </div>
      </div>
    </div>
  );
}
